import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ValidateOrgNumber,
  ValidatePhoneNumber,
  ValidatePostalCodeByCountry,
  ValidateSpace,
} from '@vinnova/validation';
import { Observable } from 'rxjs';

import { Validators } from '@angular/forms';
import { FormBuilder, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalRef } from '@vinnova/ui';
import { finalize, map, startWith } from 'rxjs';
import { CompaniesCommand } from '../../../core/commands/companies.command';
import { SearchResultsCommand } from '../../../core/commands/search-results.command';
import { WorksitesCommand } from '../../../core/commands/worksites.command';
import { Address } from '../../../core/models/address.model';
import { Country } from '../../../core/models/country.model';
import { CreateCompanyModel } from '../../../core/models/create-company.model';
import { CreateWorksiteModel } from '../../../core/models/create-worksite.model';
import { ListCompany } from '../../../core/models/list-company.model';
import { ListWorksite } from '../../../core/models/list-worksite.model';
import { WorksiteDetail } from '../../../core/models/worksite-detail.model';
import { UtilitiesQuery } from '../../../core/queries/utilities.query';
import { SearchResultsRepository } from '../../../core/repositories/search-result.repository';

@UntilDestroy()
@Component({
  selector: 'lib-create-layout',
  templateUrl: './create-layout.component.html',
  styleUrls: ['./create-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateLayoutComponent implements OnInit {
  form: FormGroup<unknown>;

  modal: ModalRef;

  countries: Country[];

  selectedCompany: ListCompany;
  selectedWorksite;
  showWorksiteSuggestions: boolean;

  isLoading = false;

  isCompanyDuplicated = false;
  isWorksiteDuplicated = false;

  isProjectPart$: Observable<boolean>;

  copiedForm: boolean;
  disableButton$: Observable<boolean>;

  @Output() navigateToSearch = new EventEmitter<void>();
  @Output() selectedWorksiteEmitter = new EventEmitter<WorksiteDetail>();

  constructor(
    private fb: FormBuilder,
    private utilitiesQuery: UtilitiesQuery,
    private route: ActivatedRoute,
    private searchResultsCommand: SearchResultsCommand,
    private searchResultsRepository: SearchResultsRepository,
    private companiesCommand: CompaniesCommand,
    private worksitesCommand: WorksitesCommand,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit() {
    const oregElement = document.querySelector('lib-oreg');
    if (oregElement) {
      oregElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }

    this.isProjectPart$ = this.route.queryParams.pipe(
      map((params) => (params['isProjectPart'] === 'true' ? true : false)),
    );

    this.countries = this.utilitiesQuery.getCountries(false);
    this.form = this.fb.group({});

    const activeCompany = this.searchResultsRepository.getActive();
    if (activeCompany) {
      this.showWorksiteSuggestions = true;
      this.selectedCompany = activeCompany;
      this.buildWorksiteForm();
    } else {
      this.buildWorksiteForm();
      this.buildCompanyForm();
    }

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe((language) => {
        this.utilitiesQuery.loadUtilities(language);
        this.countries = this.utilitiesQuery.getCountries(false);
      });
  }

  onNavigateToSearch() {
    this.searchResultsCommand.unsetActive();
    this.navigateToSearch.emit();
  }

  createCompanyAndWorksite() {
    this.isLoading = true;

    this.companiesCommand
      .createCompanyAndWorksite(
        this.buildCompanyModel(),
        this.buildWorksiteModel(),
      )
      .pipe(
        finalize(() => (this.isLoading = false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (worksite) => {
          this.selectedCompany = {
            name: this.companyForm.value.name,
          } as ListCompany;
          this.form = this.fb.group({});

          this.selectWorksiteSuccess();
          this.selectedWorksite = worksite;
          this.selectWorksite('Vinnova', worksite.providerId);
        },
        error: () => {
          this.form.enable();
        },
      });
  }

  createWorksite() {
    this.isLoading = true;

    const model = this.buildWorksiteModel();
    model.provider = this.selectedCompany.providerName;
    model.providerId = this.selectedCompany.providerId;

    this.worksitesCommand
      .createWorksite(model)
      .pipe(
        finalize(() => (this.isLoading = false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (worksite) => {
          this.form = this.fb.group({});
          this.selectWorksiteSuccess();
          this.selectedWorksite = worksite;
          this.selectWorksite('Vinnova', worksite.providerId);
        },
        error: () => {
          this.form.enable();
        },
      });
  }

  selectWorksite(provider: string, providerId: string) {
    this.showWorksiteSuggestions = false;
    this.worksitesCommand
      .selectWorksite(provider, providerId)
      .pipe(untilDestroyed(this))
      .subscribe((worksite) => {
        this.form = this.fb.group({});
        this.selectedCompany = {} as ListCompany;
        this.selectWorksiteSuccess();
        this.selectedWorksiteEmitter.emit(worksite);
      });
  }

  selectWorksiteSuccess() {
    this.searchResultsCommand.set(undefined);
  }

  resetForm() {
    if (this.companyForm) {
      this.companyForm.reset();
      this.companyForm.get('address').get('country').patchValue('SE');
    }

    this.worksiteForm.reset();
    this.worksiteForm.get('address').get('country').patchValue('SE');
  }

  buildCompanyForm() {
    this.form.addControl(
      'company',
      this.fb.group<unknown>({
        isForeign: [false],
        name: [
          undefined,
          [Validators.required, ValidateSpace, Validators.maxLength(100)],
        ],
        nationalRegistrationNumber: [
          undefined,
          [Validators.required, ValidateOrgNumber],
        ],
        vat: [undefined, [validateVAT, ValidateSpace]],
        vatMissing: [false],
        phone: [undefined, [ValidatePhoneNumber]],
        address: this.buildAddressForm(),
        comment: [undefined, [Validators.required, ValidateSpace]],
      }),
    );

    this.disableButton$ = this.companyForm.valueChanges.pipe(
      startWith(true),
      map(() => {
        this.copiedForm = false;
        return !this.companyForm?.valid;
      }),
    );
  }

  buildWorksiteForm() {
    this.form.addControl(
      'worksite',
      this.fb.group<unknown>({
        isForeign: [false],
        name: [
          undefined,
          [Validators.required, ValidateSpace, Validators.maxLength(100)],
        ],
        number: [undefined],
        phone: [undefined, [ValidatePhoneNumber]],
        address: this.buildAddressForm(),
        comment: [undefined, [Validators.required, ValidateSpace]],
      }),
    );
  }

  buildAddressForm() {
    return this.fb.group<unknown>({
      address: [undefined, [Validators.required, ValidateSpace]],
      city: [undefined, [Validators.required, ValidateSpace]],
      zipCode: [undefined, [Validators.required, ValidatePostalCodeByCountry]],
      country: ['SE', [Validators.required]],
      municipalityCode: [undefined],
      countyCode: [undefined],
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emailConditionallyRequiredValidator(formGroup: FormGroup<any>) {
    if (formGroup.value.myCheckbox) {
      return Validators.required(formGroup.get('myEmailField'))
        ? {
            myEmailFieldConditionallyRequired: true,
          }
        : undefined;
    }
  }

  onCreateWorksite() {
    this.showWorksiteSuggestions = !this.showWorksiteSuggestions;
  }

  onSelectCompany(company: ListCompany) {
    this.form.removeControl('company');
    this.searchResultsCommand.setCompany(company);
    this.searchResultsCommand.setActive(company.providerId);
    this.selectedCompany = company;
    this.showWorksiteSuggestions = true;
    this.isCompanyDuplicated = false;
  }

  onSelectWorksite(worksite: ListWorksite) {
    this.form.removeControl('worksite');
    this.selectedWorksite = worksite;
    this.selectWorksite(worksite.provider, worksite.providerId);
  }

  private buildCompanyModel(): CreateCompanyModel {
    return {
      name: this.companyForm.value.name,
      address: {
        address: this.companyForm.value.address.address,
        city: this.companyForm.value.address.city,
        zipcode: this.companyForm.value.address.zipCode,
        municipalityCode: this.companyForm.value.address.municipalityCode,
        countyCode: this.companyForm.value.address.countyCode,
      } as Address,
      country: this.companyForm.value.address.country,
      comment: this.companyForm.value.comment,
      phone: this.companyForm.value.phone,
      nationalRegistrationNumber:
        this.companyForm.value.nationalRegistrationNumber,
      vat: this.companyForm.value.vat,
    } as CreateCompanyModel;
  }

  buildWorksiteModel() {
    return {
      name: this.worksiteForm.value.name,
      address: {
        address: this.worksiteForm.value.address.address,
        city: this.worksiteForm.value.address.city,
        zipcode: this.worksiteForm.value.address.zipCode,
        municipalityCode: this.worksiteForm.value.address.municipalityCode,
        countyCode: this.worksiteForm.value.address.countyCode,
      } as Address,
      country: this.worksiteForm.value.address.country,
      comment: this.worksiteForm.value.comment,
      phone: this.worksiteForm.value.phone,
      number: this.worksiteForm.value.number,
    } as CreateWorksiteModel;
  }

  get companyForm() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.form.get('company') as FormGroup<any>;
  }

  get worksiteForm() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.form.get('worksite') as FormGroup<any>;
  }

  copyValues() {
    this.worksiteForm
      .get('isForeign')
      .patchValue(this.companyForm.value.isForeign);
    this.worksiteForm.get('name').patchValue(this.companyForm.value.name);
    this.worksiteForm.get('address').patchValue(this.companyForm.value.address);
    this.worksiteForm.get('phone').patchValue(this.companyForm.value.phone);
    this.worksiteForm.get('comment').patchValue(this.companyForm.value.comment);
  }
}

function validateVAT(control: FormControl<string>) {
  // Sätter required på vat-fältet om det är en utländsk organisation och 'VAT saknas' inte är ibockad.
  const vatMissingControl = control?.parent?.get(
    'vatMissing',
  ) as FormControl<boolean>;
  const isForeignControl = control?.parent?.get(
    'isForeign',
  ) as FormControl<boolean>;

  if (!vatMissingControl || !isForeignControl) {
    return;
  }

  return isForeignControl.value && !vatMissingControl.value && !control.value
    ? { required: true }
    : undefined;
}
